import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Plano } from '@pcp/contracts';
import { usePlans } from '../api/queries';

const STORAGE_KEY = 'pcp.nrPlano';

interface PlanContextValue {
  plans: Plano[];
  selected: Plano | undefined;
  select: (nrPlano: number) => void;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const PlanContext = createContext<PlanContextValue | null>(null);

function readStoredId(): number | undefined {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isInteger(n) ? n : undefined;
  } catch {
    return undefined;
  }
}

/** Plano selecionado no cabeçalho. Padrão: o publicado vigente; senão, o mais recente. */
export function PlanProvider({ children }: { children: ReactNode }) {
  const query = usePlans();
  const [selectedId, setSelectedId] = useState<number | undefined>(readStoredId);

  const plans = useMemo(() => query.data ?? [], [query.data]);
  const selected = useMemo(() => {
    if (!plans.length) return undefined;
    return (
      plans.find((p) => p['nr-plano'] === selectedId) ??
      plans.find((p) => p.situacao === 2) ??
      [...plans].sort((a, b) => b['nr-plano'] - a['nr-plano'])[0]
    );
  }, [plans, selectedId]);

  // Se o plano guardado deixou de existir, esquece o número
  useEffect(() => {
    if (selectedId !== undefined && plans.length && !plans.some((p) => p['nr-plano'] === selectedId))
      setSelectedId(undefined);
  }, [plans, selectedId]);

  const select = useCallback((nrPlano: number) => {
    setSelectedId(nrPlano);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, String(nrPlano));
    } catch {
      /* sem persistência */
    }
  }, []);

  const { isLoading, error, refetch } = query;
  const value = useMemo<PlanContextValue>(
    () => ({ plans, selected, select, isLoading, error, refetch: () => void refetch() }),
    [plans, selected, select, isLoading, error, refetch],
  );

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan(): PlanContextValue {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error('usePlan precisa estar dentro de <PlanProvider>');
  return ctx;
}
